/**
 * Cashback Offers authoring helpers (BONENG-432).
 *
 * `award_cashback` is a scheduled period-end result: the step trigger must be
 * the matching `cashback_period_end_{weekly|monthly}` catalog event, and the
 * authored `calculation_period` must agree so the engine does not skip the
 * campaign on a period mismatch.
 */

export const CASHBACK_RESULT_TYPE_KEY = "award_cashback";

export const CASHBACK_CALCULATION_PERIODS = ["weekly", "monthly"] as const;

export type CashbackCalculationPeriod =
  (typeof CASHBACK_CALCULATION_PERIODS)[number];

export const CASHBACK_PERIOD_EVENT_KEYS = {
  weekly: "cashback_period_end_weekly",
  monthly: "cashback_period_end_monthly",
} as const satisfies Record<CashbackCalculationPeriod, string>;

export const CASHBACK_PERIOD_EVENT_KEY_SET: ReadonlySet<string> = new Set(
  Object.values(CASHBACK_PERIOD_EVENT_KEYS),
);

export const isCashbackResult = (typeKey: string | undefined): boolean =>
  typeKey === CASHBACK_RESULT_TYPE_KEY;

export const isCashbackPeriodEvent = (eventKey: string | undefined): boolean =>
  CASHBACK_PERIOD_EVENT_KEY_SET.has(eventKey ?? "");

export const isCashbackCalculationPeriod = (
  value: unknown,
): value is CashbackCalculationPeriod =>
  value === "weekly" || value === "monthly";

export const eventKeyForCashbackPeriod = (
  period: CashbackCalculationPeriod,
): string => CASHBACK_PERIOD_EVENT_KEYS[period];

export const cashbackPeriodForEventKey = (
  eventKey: string | undefined,
): CashbackCalculationPeriod | undefined => {
  if (eventKey === CASHBACK_PERIOD_EVENT_KEYS.weekly) return "weekly";
  if (eventKey === CASHBACK_PERIOD_EVENT_KEYS.monthly) return "monthly";
  return undefined;
};

/**
 * True when max cashback is set below the min loss threshold — AC2 of
 * BONENG-432. Blank either field is valid (both optional relative to each
 * other; percentage/period are required separately).
 */
export const isCashbackMaxBelowMinLoss = (
  minLossThreshold: unknown,
  maxCashbackAmount: unknown,
): boolean => {
  const min = Number(minLossThreshold);
  const max = Number(maxCashbackAmount);
  if (!Number.isFinite(min) || !Number.isFinite(max)) return false;
  if (minLossThreshold === "" || maxCashbackAmount === "") return false;
  return max < min;
};
